/** Clauses as filter chips: a field name and a value, in plain words. */
import { ACTION_LABEL } from '@orgus/protocol';
import type { Clause } from './parse.ts';
import { windowWords } from './parse.ts';

const STATUS_LABEL: Record<string, string> = {
  live: 'Live',
  working: 'Working',
  waiting: 'Waiting for you',
  idle: 'Idle',
  ended: 'Ended',
  failed: 'Failed',
  online: 'Online',
  offline: 'Offline',
  stale: 'Not reporting',
  outdated: 'Outdated agent',
  busy: 'Busy',
  active: 'Active',
  dirty: 'Uncommitted changes',
  clean: 'Clean',
  behind: 'Behind',
  ahead: 'Ahead',
  error: 'Failed',
  ok: 'Succeeded',
  running: 'Running',
  paused: 'Paused',
  down: 'Down',
};

const FIELD_LABEL: Record<string, string> = {
  tokens: 'Tokens',
  turns: 'Turns',
  load: 'Load',
  memory: 'Memory',
  cost: 'Cost',
  subagents: 'Subagents',
  behind: 'Behind',
  changes: 'Changes',
};

const OP_WORD: Record<string, string> = { '>': 'over', '<': 'under', '>=': 'at least', '<=': 'at most', '=': 'exactly' };

function capital(s: string): string {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s;
}

function numberLabel(field: string, n: number): string {
  if (field === 'load' || field === 'memory') return `${Math.round(n * 100)}%`;
  if (field === 'cost') return `$${n}`;
  if (n >= 1e6) return `${+(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${+(n / 1e3).toFixed(1)}k`;
  return String(n);
}

/** The chip for one clause: { field: 'When', value: 'Today' }. */
export function clauseChip(c: Clause): { field: string; value: string } {
  switch (c.kind) {
    case 'status':
      return { field: 'Status', value: STATUS_LABEL[c.value] ?? capital(c.value) };
    case 'time':
      return { field: c.field === 'started' ? 'Started' : 'Active', value: capital(windowWords(c.window)) };
    case 'machine':
      return { field: 'Machine', value: c.name };
    case 'project':
      return { field: 'Project', value: c.name === 'none' ? 'No project' : c.name };
    case 'model':
      return { field: 'Model', value: capital(c.value) };
    case 'text':
      return { field: 'Text', value: `"${c.value}"` };
    case 'compare':
      return { field: FIELD_LABEL[c.field] ?? capital(c.field), value: `${OP_WORD[c.op]} ${numberLabel(c.field, c.value)}` };
    case 'actor':
      return { field: 'By', value: c.value };
    case 'action':
      return { field: 'Action', value: ACTION_LABEL[c.value] };
    case 'has':
      return { field: 'With', value: c.value };
    case 'origin':
      return { field: 'Origin', value: 'Started here' };
  }
}
